function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { UPDATE_NAME_DELAY } from '@repo/constants';
import { LogoMobileZephyrIcon } from '@repo/custom-icons';
import { getUniqueOrganizationName } from '@repo/domains-organization';
import { Button } from '@repo/ui-primitives/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@repo/ui-primitives/dialog';
import { Input } from '@repo/ui-primitives/input';
import { Label } from '@repo/ui-primitives/label';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ORGANIZATION_NAME_VALIDATOR } from 'ze-api-contract/validation';
export function AddOrganizationModal(param) {
    let { open, onClose, onSave } = param;
    const queryClient = useQueryClient();
    const [loading, setLoading] = useState(false);
    const [organizationUniqueName, setOrganizationUniqueName] = useState('');
    const { formState, reset, register, watch, getValues } = useForm({
        defaultValues: {
            name: ''
        },
        mode: 'onChange'
    });
    const { name } = watch();
    const close = ()=>{
        onClose();
        reset();
    };
    const onSubmit = ()=>{
        const name = getValues().name;
        onSave(name, organizationUniqueName);
        onClose();
        reset();
    };
    const updateUniqueName = debounce(async ()=>{
        if (!formState.errors.name && name) {
            setLoading(true);
            const uniqueName = await getUniqueOrganizationName(queryClient, name).finally(()=>{
                setLoading(false);
            });
            setOrganizationUniqueName(uniqueName);
        } else {
            setOrganizationUniqueName('');
        }
        setLoading(false);
    }, UPDATE_NAME_DELAY);
    const helperText = useMemo(()=>{
        if (formState.errors.name) {
            return formState.errors.name.message;
        }
        if (organizationUniqueName) {
            return "Organization name will be: ".concat(organizationUniqueName);
        }
        return '';
    }, [
        formState,
        organizationUniqueName
    ]);
    useEffect(()=>{
        void updateUniqueName();
        return ()=>{
            updateUniqueName.cancel();
        };
    }, [
        formState,
        name,
        updateUniqueName
    ]);
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: (isOpen)=>!isOpen && close(),
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            className: "max-w-3xl p-10",
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    className: "text-center items-center",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "mb-4",
                            children: /*#__PURE__*/ _jsx(LogoMobileZephyrIcon, {})
                        }),
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            className: "text-[28px] font-bold leading-[120%]",
                            children: "Add New Organization"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col gap-2 mt-4",
                    children: [
                        /*#__PURE__*/ _jsxs(Label, {
                            htmlFor: "organization-name",
                            className: "text-sm font-bold text-muted-foreground",
                            children: [
                                "Organization Name ",
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-destructive",
                                    children: "*"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Input, _object_spread({
                            id: "organization-name",
                            type: "text",
                            placeholder: "Enter organization name",
                            "aria-invalid": !!formState.errors.name
                        }, register('name', _object_spread({}, ORGANIZATION_NAME_VALIDATOR)))),
                        helperText && /*#__PURE__*/ _jsx("p", {
                            className: "text-sm ".concat(formState.errors.name ? 'text-destructive' : 'text-muted-foreground'),
                            children: helperText
                        }),
                        /*#__PURE__*/ _jsx(DialogDescription, {
                            className: "text-sm text-muted-foreground mt-2",
                            children: "The uniqueness of the name will be verified by the system. Later, in the Organization settings, you can provide a display name for use across the application."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(DialogFooter, {
                    className: "mt-6 flex gap-4",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            onClick: close,
                            className: "flex-1",
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "default",
                            onClick: onSubmit,
                            disabled: !formState.isValid || !organizationUniqueName || loading,
                            className: "flex-1",
                            children: "Add Organization"
                        })
                    ]
                })
            ]
        })
    });
}
